import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TargetIcon, HandshakeIcon, TrendingUpIcon, CheckCircle2Icon, ArrowRightIcon, DownloadIcon, MapPinIcon, UsersIcon, DatabaseIcon, NetworkIcon, BuildingIcon, MailIcon } from 'lucide-react';
const useCases = [{
  id: 'lead-gen',
  title: 'Lead generation',
  icon: TargetIcon,
  description: 'Build targeted prospect lists for your sales team',
  features: ['Find qualified leads in any location', 'Filter by business type and size', 'Export ready-to-use contact lists', 'Integrate with your CRM'],
  visual: 'crm'
}, {
  id: 'partnerships',
  title: 'Partnership development',
  icon: HandshakeIcon,
  description: 'Identify and connect with potential partners',
  features: ['Discover complementary businesses', 'Research local market opportunities', 'Find franchise expansion locations', 'Build supplier/vendor networks'],
  visual: 'network'
}, {
  id: 'research',
  title: 'Market research',
  icon: TrendingUpIcon,
  description: 'Analyze local business landscapes and trends',
  features: ['Competitive intelligence gathering', 'Market penetration analysis', 'Location-based opportunity mapping', 'Industry trend identification'],
  visual: 'chart'
}];
function CrmVisual() {
  const rows = [{
    name: "Bella's Bakery",
    type: 'Bakery',
    status: 'Verified'
  }, {
    name: 'Peak Fitness Co.',
    type: 'Gym',
    status: 'Verified'
  }, {
    name: 'Urban Dental Care',
    type: 'Dentist',
    status: 'Pending'
  }];
  return <motion.div key="crm" initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -10
  }} transition={{
    duration: 0.4
  }} className="w-full max-w-sm mx-auto" data-id="element-3087">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden" data-id="element-3088">
        <div className="bg-slate-800 px-4 py-2.5 border-b border-slate-700 flex items-center justify-between" data-id="element-3089">
          <div className="flex gap-1.5" data-id="element-3090">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" data-id="element-3091" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" data-id="element-3092" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" data-id="element-3093" />
          </div>
          <motion.div animate={{
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="text-[10px] font-medium text-blue-400 flex items-center gap-1" data-id="element-3094">
            <DownloadIcon className="w-3 h-3" data-id="element-3095" /> Export to CRM
          </motion.div>
        </div>
        {/* Table header */}
        <div className="px-4 py-2 border-b border-slate-800 flex items-center text-[9px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-3096">
          <span className="flex-1" data-id="element-3097">Business</span>
          <span className="w-16 text-center" data-id="element-3098">Type</span>
          <span className="w-16 text-right" data-id="element-3099">Status</span>
        </div>
        <div className="p-2 space-y-1" data-id="element-3100">
          {rows.map((row, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.4,
          delay: 0.2 + i * 0.15
        }} className="flex items-center px-2 py-2 rounded-lg hover:bg-slate-800/60 transition-colors" data-id="element-3101">
              <div className="flex items-center gap-2.5 flex-1 min-w-0" data-id="element-3102">
                <motion.div animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.5
            }} className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0" data-id="element-3103">
                  <BuildingIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-3104" />
                </motion.div>
                <div className="min-w-0" data-id="element-3105">
                  <div className="text-xs font-semibold text-slate-200 truncate" data-id="element-3106">
                    {row.name}
                  </div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1" data-id="element-3107">
                    <MailIcon className="w-2.5 h-2.5" data-id="element-3108" /> Email available
                  </div>
                </div>
              </div>
              <span className="w-16 text-center text-[10px] text-slate-400 font-medium" data-id="element-3109">
                {row.type}
              </span>
              <div className="w-16 flex justify-end" data-id="element-3110">
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${row.status === 'Verified' ? 'bg-green-500/15 text-green-400 border border-green-500/20' : 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/20'}`} data-id="element-3111">
                  {row.status}
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>
    </motion.div>;
}
function NetworkVisual() {
  // All positions as percentages (x% of width, y% of height)
  const nodes = [{
    x: 12.5,
    y: 20,
    icon: DatabaseIcon,
    size: 'sm' as const,
    delay: 0
  }, {
    x: 50,
    y: 47.5,
    icon: NetworkIcon,
    size: 'lg' as const,
    delay: 0.2
  }, {
    x: 87.5,
    y: 20,
    icon: MapPinIcon,
    size: 'sm' as const,
    delay: 0.4
  }, {
    x: 30,
    y: 77.5,
    icon: BuildingIcon,
    size: 'sm' as const,
    delay: 0.3
  }, {
    x: 70,
    y: 77.5,
    icon: UsersIcon,
    size: 'sm' as const,
    delay: 0.5
  }];
  const lines = [{
    x1: 12.5,
    y1: 20,
    x2: 50,
    y2: 47.5
  }, {
    x1: 50,
    y1: 47.5,
    x2: 87.5,
    y2: 20
  }, {
    x1: 30,
    y1: 77.5,
    x2: 50,
    y2: 47.5
  }, {
    x1: 50,
    y1: 47.5,
    x2: 70,
    y2: 77.5
  }];
  return <motion.div key="network" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4
  }} className="relative w-full h-full" data-id="element-3112">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-3113">
        {lines.map((line, i) => <motion.line key={i} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} stroke="#3b82f6" strokeWidth="0.5" strokeDasharray="2 1.5" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 0.4
      }} transition={{
        duration: 0.8,
        delay: 0.3 + i * 0.15
      }} data-id="element-3114" />)}
      </svg>
      {nodes.map((node, i) => {
      const Icon = node.icon;
      const isCenter = node.size === 'lg';
      return <motion.div key={i} initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4,
        delay: node.delay,
        type: 'spring',
        stiffness: 200
      }} className="absolute" style={{
        left: `${node.x}%`,
        top: `${node.y}%`,
        transform: 'translate(-50%, -50%)'
      }} data-id="element-3115">
            {/* Pulse ring */}
            <motion.div animate={{
          opacity: [0, 0.3, 0],
          scale: [0.8, 1.6, 0.8]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          delay: node.delay + 1
        }} className={`absolute inset-0 rounded-full border border-blue-500 ${isCenter ? 'scale-150' : ''}`} data-id="element-3116" />
            <motion.div animate={isCenter ? {} : {
          y: [0, -4, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          delay: node.delay,
          ease: 'easeInOut'
        }} className={`rounded-full flex items-center justify-center ${isCenter ? 'w-14 h-14 bg-blue-600 shadow-xl shadow-blue-600/30 border-2 border-blue-500' : 'w-10 h-10 bg-slate-800 border-2 border-slate-700 shadow-lg'}`} data-id="element-3117">
              <Icon className={`${isCenter ? 'w-6 h-6 text-white' : 'w-4 h-4 text-slate-400'}`} data-id="element-3118" />
            </motion.div>
          </motion.div>;
    })}
    </motion.div>;
}
function ChartVisual() {
  const bars = [{
    height: 35,
    label: 'Q1'
  }, {
    height: 55,
    label: 'Q2'
  }, {
    height: 40,
    label: 'Q3'
  }, {
    height: 80,
    label: 'Q4'
  }, {
    height: 60,
    label: 'Q5'
  }, {
    height: 72,
    label: 'Q6'
  }];
  return <motion.div key="chart" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4
  }} className="w-full h-full flex flex-col px-8 pt-6 pb-4" data-id="element-3119">
      {/* Y-axis grid lines */}
      <div className="flex-1 relative" data-id="element-3120">
        {[0, 1, 2, 3].map(i => <div key={i} className="absolute w-full border-t border-slate-800/50" style={{
        top: `${i * 33}%`
      }} data-id="element-3121" />)}
        <div className="absolute inset-0 flex items-end justify-center gap-4" data-id="element-3122">
          {bars.map((bar, i) => <div key={i} className="flex-1 flex flex-col items-center justify-end h-full relative" data-id="element-3123">
              <motion.div initial={{
            height: 0
          }} animate={{
            height: `${bar.height}%`
          }} transition={{
            duration: 0.6,
            delay: 0.2 + i * 0.1,
            ease: [0.34, 1.56, 0.64, 1]
          }} className={`w-full max-w-[40px] rounded-t-md relative ${i === 3 ? 'bg-blue-500' : 'bg-slate-700'}`} data-id="element-3124">
                {i === 3 && <motion.div initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 1
            }} className="absolute -top-7 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-md whitespace-nowrap shadow-lg shadow-blue-600/30" data-id="element-3125">
                    +24%
                  </motion.div>}
                {i === 3 && <motion.div animate={{
              opacity: [0.3, 0.6, 0.3]
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} className="absolute inset-0 rounded-t-md bg-blue-400/20" data-id="element-3126" />}
              </motion.div>
            </div>)}
        </div>
      </div>
      {/* X-axis labels */}
      <div className="flex justify-center gap-4 mt-2 pt-2 border-t border-slate-800/50" data-id="element-3127">
        {bars.map((bar, i) => <motion.span key={i} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5 + i * 0.05
      }} className={`flex-1 text-center text-[9px] font-medium ${i === 3 ? 'text-blue-400' : 'text-slate-600'}`} data-id="element-3128">
            {bar.label}
          </motion.span>)}
      </div>
    </motion.div>;
}
export function PowerfulUseCases() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeCase = useCases[activeIndex];
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-3129">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-3130">
        <div className="text-center max-w-3xl mx-auto mb-16 relative z-10" data-id="element-3131">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-3xl md:text-4xl font-bold mb-4 text-slate-900" data-id="element-3132">
            Powerful <span className="text-blue-600" data-id="element-3133">use cases</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-lg text-slate-500" data-id="element-3134">
            Transform local business data into actionable insights and revenue
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 relative z-10" data-id="element-3135">
          {/* Left Column: Interactive Tabs */}
          <div className="lg:col-span-5 space-y-4" data-id="element-3136">
            {useCases.map((useCase, index) => {
            const isActive = index === activeIndex;
            const Icon = useCase.icon;
            return <motion.button key={useCase.id} onClick={() => setActiveIndex(index)} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} className={`w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 group ${isActive ? 'bg-blue-50/50 border-blue-500 shadow-lg shadow-blue-500/10' : 'bg-white border-slate-100 hover:border-blue-200 hover:bg-slate-50'}`} data-id="element-3137">
                  <div className="flex items-start gap-4" data-id="element-3138">
                    <div className={`p-3 rounded-xl transition-colors duration-300 shrink-0 ${isActive ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'}`} data-id="element-3139">
                      <Icon className="w-6 h-6" data-id="element-3140" />
                    </div>
                    <div data-id="element-3141">
                      <h3 className={`text-xl font-bold mb-1 transition-colors duration-300 ${isActive ? 'text-blue-900' : 'text-slate-900 group-hover:text-blue-700'}`} data-id="element-3142">
                        {useCase.title}
                      </h3>
                      <p className={`text-sm transition-colors duration-300 ${isActive ? 'text-blue-700/80' : 'text-slate-500'}`} data-id="element-3143">
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                </motion.button>;
          })}
          </div>

          {/* Right Column: Dynamic Content Area */}
          <div className="lg:col-span-7" data-id="element-3144">
            <div className="bg-slate-900 rounded-3xl h-full relative overflow-hidden shadow-2xl shadow-slate-900/20 border border-slate-800" data-id="element-3145">
              {/* Dynamic Visual Header */}
              <div className="h-52 bg-slate-800/30 border-b border-slate-800 relative overflow-hidden flex items-center justify-center" data-id="element-3146">
                <AnimatePresence mode="wait" data-id="element-3147">
                  {activeCase.visual === 'crm' && <CrmVisual data-id="element-3148" />}
                  {activeCase.visual === 'network' && <NetworkVisual data-id="element-3149" />}
                  {activeCase.visual === 'chart' && <ChartVisual data-id="element-3150" />}
                </AnimatePresence>
              </div>

              {/* Features Grid */}
              <div className="p-8 flex-1 flex flex-col justify-center" data-id="element-3151">
                <AnimatePresence mode="wait" data-id="element-3152">
                  <motion.div key={activeIndex} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} className="grid sm:grid-cols-2 gap-4" data-id="element-3153">
                    {activeCase.features.map((feature, idx) => <motion.div key={idx} initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: idx * 0.08
                  }} className="flex items-start gap-3 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 transition-colors" data-id="element-3154">
                        <CheckCircle2Icon className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" data-id="element-3155" />
                        <span className="text-sm font-medium text-slate-300" data-id="element-3156">
                          {feature}
                        </span>
                      </motion.div>)}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-8 pt-6 border-t border-slate-800 flex justify-end" data-id="element-3157">
                  <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2 text-sm font-bold text-blue-400 hover:text-blue-300 transition-colors" data-id="element-3158">
                    Explore {activeCase.title.toLowerCase()}
                    <ArrowRightIcon className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-3159" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}